import { useRequiredContext } from "@redotech/react-util/context";
import { shippingCarriers } from "@redotech/redo-model/carriers";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { FormMultiSelectDropdown } from "@redotech/redo-web/select-dropdown";
import { FormTextInput } from "@redotech/redo-web/text-input";
import { InputSchema, groupInput, input } from "@redotech/ui/form";
import { arrayEqual, stringEqual } from "@redotech/util/equal";
import classNames from "classnames";
import { memo } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../../../app/auth";
import { LABEL_RATE_AUTHORIZED_EMAILS } from "../../../../../returns/constants";
import { DefaultWeightDisplay } from "../../label-rate/default-weight-display";
import { LinkCard } from "../component/link-card";
import { SettingCard } from "../component/setting-card";

export const shippingForm = groupInput({
  carrierOverride: input<string[]>({ equal: arrayEqual(stringEqual) }),
  rateSourceSelection: groupInput({ enabled: input<boolean>() }),
  insured: input<boolean>(),
  passThroughReturnLabelAdjustments: input<boolean>(),
  pickupEnabled: input<boolean>(),
  pickupEnabledForHeavyItems: input<boolean>(),
  pickupMaxLabelUpgradeRange: input<string>(),
  canEditReturnAddress: input<boolean>(),
  generateQrCodes: input<boolean>(),
  qrCodeOnlyShipping: input<boolean>(),
  fedexQrCodesEnabledForMerchantRates: input<boolean>(),
  useFixedPackageDimensions: input<boolean>(),
  fixedPackageDimensions: groupInput({
    length: input<string>(),
    width: input<string>(),
    height: input<string>(),
    weight: input<string>(),
  }),
});

export type ShippingForm = InputSchema.Input<typeof shippingForm>;

export type ShippingValue = InputSchema.Value<typeof shippingForm>;

export const shippingDefault: ShippingValue = {
  carrierOverride: [],
  rateSourceSelection: { enabled: false },
  insured: false,
  passThroughReturnLabelAdjustments: false,
  pickupEnabled: false,
  pickupEnabledForHeavyItems: false,
  pickupMaxLabelUpgradeRange: "",
  canEditReturnAddress: false,
  generateQrCodes: false,
  qrCodeOnlyShipping: false,
  fedexQrCodesEnabledForMerchantRates: false,
  useFixedPackageDimensions: false,
  fixedPackageDimensions: { length: "", width: "", height: "", weight: "" },
};

export const ReturnShippingSettings = memo(function ReturnShippingSettings({
  input,
  redoMaxLabelPriceInput,
}: {
  input: ShippingForm;
  redoMaxLabelPriceInput?: { setValue: (value: string) => void; value: string };
}) {
  const auth = useRequiredContext(AuthContext);
  const canSeeRedoMaxLabelPrice =
    redoMaxLabelPriceInput && LABEL_RATE_AUTHORIZED_EMAILS.includes(auth.email);
  const navigate = useNavigate();
  const {
    carrierOverride,
    rateSourceSelection,
    passThroughReturnLabelAdjustments,
    pickupEnabled,
    pickupEnabledForHeavyItems,
    pickupMaxLabelUpgradeRange,
    insured,
    canEditReturnAddress,
    generateQrCodes,
    qrCodeOnlyShipping,
    fedexQrCodesEnabledForMerchantRates,
    useFixedPackageDimensions,
    fixedPackageDimensions,
  } = input.inputs;

  return (
    <Flex dir="column" gap="3xl">
      <FormMultiSelectDropdown
        description={
          carrierOverride.value.length > 0
            ? "Enabled shipping carriers. This carrier override takes precedence over QR code only shipping."
            : "Enabled shipping carriers"
        }
        display={(value) => {
          if (!value.length || value.length === shippingCarriers.length) {
            return "All";
          }
          const names = value.map(
            (value) =>
              shippingCarriers.find((carrier) => carrier.value === value)?.name,
          );
          return names.join(", ");
        }}
        input={carrierOverride}
        label="Carriers"
        options={shippingCarriers.map((carrier) => carrier.value)}
      >
        {(option) =>
          shippingCarriers.find((carrier) => carrier.value === option)?.name
        }
      </FormMultiSelectDropdown>

      <RedoToggle
        description="Allow merchants to choose between using Redo rates or their own carrier accounts for return rate shopping in the locations flow. This setting may affect margin and reliability of getting return labels."
        label="Rate source selection"
        setValue={rateSourceSelection.inputs.enabled.setValue}
        size="sm"
        value={rateSourceSelection.value.enabled}
      />

      <RedoToggle
        description="Enable shipping insurance. Increases label price by 0.5% of items value (min $0.50)."
        label="Shipping insurance"
        setValue={insured.setValue}
        size="sm"
        value={insured.value}
      />
      <RedoToggle
        description="Pass through carrier label adjustments to merchants. When enabled, any adjustments from the carrier (such as address corrections or dimensional weight changes) will be charged to the merchant."
        label="Pass through return label adjustments"
        setValue={passThroughReturnLabelAdjustments.setValue}
        size="sm"
        value={passThroughReturnLabelAdjustments.value}
      />

      <RedoToggle
        description="Offer return package pickup, for the cost of label upgrade plus $2"
        label="Return pickup"
        setValue={pickupEnabled.setValue}
        size="sm"
        value={pickupEnabled.value}
      />

      {pickupEnabled.value && (
        <>
          <RedoToggle
            description="Allow package pickup for all items regardless of weight limits (over 9 lbs)"
            label="Allow package pickup for heavy items"
            setValue={pickupEnabledForHeavyItems.setValue}
            size="sm"
            value={pickupEnabledForHeavyItems.value}
          />

          <RedoTextInput
            description="Maximum range for package pickup label upgrade"
            IconLeading={() => "$"}
            inputTypeHint="number"
            label="Pickup max label upgrade range"
            min={0.01}
            setValue={pickupMaxLabelUpgradeRange.setValue}
            step={0.01}
            value={pickupMaxLabelUpgradeRange.value}
          />
        </>
      )}
      {canSeeRedoMaxLabelPrice && redoMaxLabelPriceInput && (
        <RedoTextInput
          description="Maximum price (in USD) that Redo will cover for return labels when a return is covered"
          IconLeading={() => "$"}
          inputTypeHint="number"
          label="Redo max label coverage price"
          max={2000}
          min={1}
          setValue={redoMaxLabelPriceInput.setValue}
          value={redoMaxLabelPriceInput.value}
        />
      )}
      <RedoToggle
        description="Allows Merchant to change return address during return approval"
        label="Change return address"
        setValue={canEditReturnAddress.setValue}
        size="sm"
        value={canEditReturnAddress.value}
      />
      <RedoToggle
        description="Generate QR codes for return labels when the carrier supports them. Required for QR code only shipping to work."
        label="Generate QR codes"
        setValue={generateQrCodes.setValue}
        size="sm"
        value={generateQrCodes.value}
      />
      <RedoToggle
        description={
          carrierOverride.value.length > 0
            ? "Restrict rates to QR-supporting carriers and only show the QR code to customers. The label is shown as a fallback if QR generation fails. This may increase label costs since cheaper non-QR carriers will be excluded. Currently inactive because a carrier override is set above."
            : "Restrict rates to QR-supporting carriers and only show the QR code to customers. The label is shown as a fallback if QR generation fails. This may increase label costs since cheaper non-QR carriers will be excluded."
        }
        label="QR code only shipping"
        setValue={qrCodeOnlyShipping.setValue}
        size="sm"
        value={qrCodeOnlyShipping.value}
      />
      <RedoToggle
        description="Enable FedEx QR code generation for merchant-created labels. Merchants must ensure FedEx QR code support is enabled on their EasyPost account."
        label="FedEx QR codes (merchant labels)"
        setValue={fedexQrCodesEnabledForMerchantRates.setValue}
        size="sm"
        value={fedexQrCodesEnabledForMerchantRates.value}
      />

      <SettingCard>
        <Flex align="stretch" dir="column" gap="none">
          <LinkCard
            onClick={() => navigate("../label-rate-overrides")}
            subtitle="Configure merchant-specific rate calculation rules"
            title="Label rate calculation overrides"
          />
        </Flex>
      </SettingCard>

      <DefaultWeightDisplay />
      <RedoToggle
        description="Use standard dimensions for all return labels instead of calculating from product weight"
        label="Use fixed package dimensions"
        setValue={useFixedPackageDimensions.setValue}
        size="sm"
        value={useFixedPackageDimensions.value}
      />
      {useFixedPackageDimensions.value && (
        <div className={gridCss.grid}>
          <div className={classNames(gridCss.span12, gridCss.span3M)}>
            <FormTextInput
              input={fixedPackageDimensions.inputs.length}
              label="Length (in)"
              step="0.1"
              type="number"
            />
          </div>
          <div className={classNames(gridCss.span12, gridCss.span3M)}>
            <FormTextInput
              input={fixedPackageDimensions.inputs.width}
              label="Width (in)"
              step="0.1"
              type="number"
            />
          </div>
          <div className={classNames(gridCss.span12, gridCss.span3M)}>
            <FormTextInput
              input={fixedPackageDimensions.inputs.height}
              label="Height (in)"
              step="0.1"
              type="number"
            />
          </div>
          <div className={classNames(gridCss.span12, gridCss.span3M)}>
            <FormTextInput
              input={fixedPackageDimensions.inputs.weight}
              label="Weight (oz)"
              step="0.1"
              type="number"
            />
          </div>
        </div>
      )}
    </Flex>
  );
});
